import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Modal partagé de l'application.
 *
 * Standardise l'apparence de toutes les fenêtres modales (overlay, carte,
 * en-tête avec titre + fermeture, corps défilable, pied d'actions) en
 * s'alignant sur le gabarit des pages publiques. Accessible par défaut :
 * fermeture par Échap et clic hors panneau, piège de focus et attributs
 * ARIA fournis par Headless UI.
 *
 * Le comportement d'ouverture/fermeture reste piloté par le parent via
 * `open` et `onClose` ; ce composant n'introduit aucune logique métier.
 *
 * Props :
 *  - open        : booléen d'affichage (défaut true, pour les modals montés
 *                  de façon conditionnelle par le parent).
 *  - onClose     : callback de fermeture (Échap, clic overlai, bouton ✕).
 *  - title       : titre de l'en-tête (masque l'en-tête si absent).
 *  - children    : corps de la modale.
 *  - footer      : contenu du pied (boutons d'action) ; masqué si absent.
 *  - size        : sm | md | lg | xl.
 *  - bodyClassName: classes du corps (défaut px-6 py-5) ; passer '' quand le
 *                  contenu fournit déjà son propre padding.
 *  - initialFocus: ref optionnelle pour le focus initial.
 */

const SIZES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
};

const Modal = ({
  open = true,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  bodyClassName = 'px-6 py-5',
  initialFocus,
}) => {
  return (
    <Transition.Root show={open} as={Fragment} appear>
      <Dialog as="div" className="relative z-50" onClose={onClose} initialFocus={initialFocus}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative w-full ${SIZES[size] || SIZES.md} transform overflow-hidden rounded-xl bg-white text-left align-middle shadow-xl transition-all sm:my-8`}
              >
                {title != null && title !== '' && (
                  <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
                    <Dialog.Title className="text-lg font-semibold leading-6 text-gray-900">
                      {title}
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label="Fermer"
                      className="-m-1.5 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                    >
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                )}

                <div className={`max-h-[70vh] overflow-y-auto ${bodyClassName}`}>{children}</div>

                {footer && (
                  <div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;
